import {
  UserInterestPreference,
  useGetUserInterestPreferencesFunctionRequest,
  useUpdateUserInterestsFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { useEffect, useState } from "react";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import Button from "shared/UI/Buttons/Button";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import { t } from "i18next";
import { useUserContext } from "core/state/UserContext/UserContext";
import { NarrowContainer } from "shared/UI/LayoutComponents/LayoutComponents";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";
import { Checkbox } from "shared/UI/Checkbox/Checkbox"

export const Preferences = () => {
  const { modalNotify } = useNotifyModalContext();
  const [interestPreferences, setInterestPreferences] = useState<UserInterestPreference[]>([]);
  const { data, error, isLoading, mutate: refresh } = useGetUserInterestPreferencesFunctionRequest({});
  const { trigger } = useUpdateUserInterestsFunctionRequest({});
  const { user, updateUserInterests } = useUserContext();

  useEffect(() => {
    if (data?.interestPreferences) {
      const sortedPreferences = [...data.interestPreferences].sort((a, b) => a.name.localeCompare(b.name));
      setInterestPreferences(sortedPreferences);
    }
  }, [data]);

  const savePreference = async () => {
    const interestCategoryIds = interestPreferences.filter((interest) => interest.isSelected).map((interest) => interest.categoryId);
    try {
      const response = await trigger({ body: { interestCategoryIds } });
      updateUserInterests(response?.data.interests ?? []);
      await refresh();
      modalNotify("success", t("preferences.updateSuccess"));
    } catch {
      modalNotify("error", t("preferences.failureTitle"), t("preferences.updateFailure"), t("preferences.failureCta"));
    }
  };

  const canSavePreferences = () => {
    const selectedCategories = interestPreferences
      .filter((interest) => interest.isSelected)
      .map((interest) => interest.categoryId)
      .sort((a, b) => a.localeCompare(b));
    const previouslySelectedCategories = user?.interests?.map((interest) => interest.categoryId).sort();
    return selectedCategories.join() === previouslySelectedCategories?.join();
  };

  return (
    <>
      <PageTitle title={t("pageTitle.account.preferences")} />
      <PageHeader title={t("preferences.heading")} narrowView hideForMobile hideBackForDesktop />
      <NarrowContainer>
        <ViewStateWrapper loading={isLoading} error={!!error} errorMessage={t("preferences.error")} onRetry={refresh} isFullScreenView={true}>
          <div className="space-y-6">
            <div className="space-y-4">
              <p className="col-start-1">{t("preferences.intro")}</p>
              <div>
                {interestPreferences.map((interest, index) => (
                  <div className="interest-preferences py-2" key={index}>
                    <Checkbox
                      checked={interest.isSelected}
                      onChange={(e) => {
                        const preferencesToUpdate = [...interestPreferences];
                        preferencesToUpdate[index].isSelected = e.target.checked;
                        setInterestPreferences(preferencesToUpdate);
                      }}
                      label={interest.name}
                    />
                  </div>
                ))}
              </div>
            </div>
            <Button disabled={canSavePreferences()} text={t("preferences.save")} onClick={async () => savePreference()} />
          </div>
        </ViewStateWrapper>
      </NarrowContainer>
    </>
  );
};
